import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useDispatch, useSelector } from 'react-redux';
import { constants } from '@clubmed/utils';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  useCriteriaDisplayContext,
  ACTIONS,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger';
import { OPEN_CRITERIA_TRANSPORT_EVENT } from 'Components/widgets/criteria/criteria-transport/constants';
import { FilterTransport } from 'Components/widgets/criteria/criteria-transport/filter-transport/FilterTransport';
import type { ComponentProps } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { useAppContext } from 'Context/ReactAppContext';
import { useSyncDepartureCity } from 'Src/hooks/use-sync-departure-city/useSyncDepartureCity';
import { t } from 'Src/i18n';
import { changeDepartureCity, updateCustomFilters } from 'Store/faceting/facetingSearch.action';
import { customFiltersSelector, filtersSelector } from 'Store/faceting/faceting.selector';
import { getDepartureCity } from 'Store/trip-criteria/tripCriteria.selector';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { DEPARTURE_CITY_FACET_ID, FLIGHT_DURATION_FACET_ID } from 'Utils/faceting/constants';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
const { NO_TRANSPORT_VALUE } = constants;
type CriteriaTransportProps = {
  departureCitiesOptions: DepartureCities;
  initialTransport?: string | null | undefined;
  isJoyview?: boolean;
  isLoading?: boolean;
  transportIcon?: string;
  withValidDepartureCity?: boolean;
};
export const CriteriaTransport = ({
  departureCitiesOptions,
  initialTransport,
  isJoyview = false,
  isLoading = false,
  transportIcon = ICONS.TRANSPORT,
  withValidDepartureCity,
}: CriteriaTransportProps) => {
  const appContext = useAppContext();
  const isInitialMount = useRef(true);
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const dispatch = useDispatch();
  const { isAppMounted } = useResponsive();
  const departureCity = useSelector(getDepartureCity);
  const facetingCustomFilters = useSelector(customFiltersSelector);
  const facetingFilters = useSelector(filtersSelector);
  const track = useTracking({
    actionGA: criteriaContext.trackingActionName,
  });
  // Store update effect
  const { inputDepartureCity, setInputDepartureCity } = useSyncDepartureCity({
    departureCitiesOptions,
    departureCity,
    initialTransport,
    onChange: (newDepartureCity: string) => {
      setCriteriaContext({
        criteriaTransportIsSet: Boolean(newDepartureCity),
      });
    },
    withValidDepartureCity,
  });
  useEffect(() => {
    if (!isInitialMount.current && inputDepartureCity) {
      saveTransport();
    }

    isInitialMount.current = false;
  }, [inputDepartureCity]);
  const noDepartureCitiesOptions = [
    {
      isDisabled: true,
      isSelected: true,
      label: t('booking.priceCalendar.priceStatus.noFlight'),
      value: '',
    },
  ];

  const openCriteriaEventHandler = () => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaTransport',
    });
  };

  // Open/Close effect
  useEffect(() => {
    document.addEventListener(OPEN_CRITERIA_TRANSPORT_EVENT, openCriteriaEventHandler);
    return () =>
      document.removeEventListener(OPEN_CRITERIA_TRANSPORT_EVENT, openCriteriaEventHandler);
  }, []);

  const resetFlightDurationActiveFacetOptions = () =>
    dispatch(
      updateCustomFilters(
        [...facetingCustomFilters].filter(({ id }) => id !== FLIGHT_DURATION_FACET_ID),
      ),
    );

  const saveTransport = () => {
    if (inputDepartureCity !== departureCity || !inputDepartureCity) {
      criteriaContext.setFacetHasSeeMore(false);
      dispatch(changeDepartureCity(inputDepartureCity || NO_TRANSPORT_VALUE));

      if (!inputDepartureCity) {
        resetFlightDurationActiveFacetOptions();
      }
    }

    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaTransport',
    });
  };

  const getIcon = () => {
    if (departureCity === NO_TRANSPORT_VALUE) {
      return ICONS.NO_TRANSPORT;
    }

    return transportIcon;
  };

  const getLabel = () => {
    if (!isAppMounted || !criteriaContext.criteriaTransportIsSet) {
      return t('faceting.transport.departingFrom');
    }

    const selectedOption = departureCitiesOptions.find((item) => item.value === departureCity);

    if (!selectedOption) {
      const departureCityFilter = facetingFilters.find(({ id }) => id === DEPARTURE_CITY_FACET_ID);
      return departureCityFilter ? departureCityFilter.label : '';
    }

    return selectedOption.label;
  };

  const buttonIsLoading =
    !departureCitiesOptions.length && isLoading && criteriaContext.criteriaTransportIsSet;
  const buttonLabel = <span className="hidden flex-1 truncate lg:block">{getLabel()}</span>;

  const trackDepartureCityChange = (newDepartureCity: string) => {
    if (newDepartureCity) {
      const labelGA = `departureCity_${newDepartureCity}`;

      if (criteriaContext.trackingActionName) {
        track({
          labelGA,
        });
      }
    }
  };

  const buttonsSlot = (
    <div className="text-12">
      <div className="mb-15 flex gap-15 text-16 font-bold">
        <Icon className="shrink-0 text-sienna" name={ICONS.ALL_INCLUSIVE} />
        {t('faceting.transport.departureCities.reinsurance')}
      </div>
      <div className="mb-10">{t('faceting.transport.departureCities.guarantee')}</div>
      <div>{t('faceting.transport.departureCities.schedule')}</div>
    </div>
  );
  const onOpen = useCallback(() => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaTransport',
    });
  }, []);
  const onClear = useCallback(() => {
    dispatch(changeDepartureCity(NO_TRANSPORT_VALUE));
    resetFlightDurationActiveFacetOptions();
  }, []);
  return !isFeatureFlipped(appContext)('faceting.hideCriteriaTransport') ? (
    <CriteriaTrigger
      dataTestId="CriteriaTransport-trigger"
      buttonLabel={buttonLabel}
      buttonIcon={getIcon()}
      buttonIsFilled={
        criteriaContext.criteriaTransportIsSet && departureCity !== NO_TRANSPORT_VALUE
      }
      buttons={[
        {
          children: t('faceting.reset'),
          onClick: () => {
            setInputDepartureCity('');
          },
          theme: 'link',
        },
        {
          children: t('faceting.validate'),
          onClick: saveTransport,
        },
      ]}
      buttonsSlot={buttonsSlot}
      hasSwitch
      isJoyview={isJoyview}
      isLoading={buttonIsLoading}
      isOpen={criteriaDisplayContext.criteriaTransportIsOpen}
      onClear={onClear}
      onClose={saveTransport}
      onOpen={onOpen}
      trackingLabel="departureCity"
    >
      <FilterTransport
        {...asWidgetProps<ComponentProps<typeof FilterTransport>>({
          changeDepartureCity: (newDepartureCity: string) => {
            setInputDepartureCity(newDepartureCity);
            trackDepartureCityChange(newDepartureCity);
          },
          className: 'h-full',
          dataTestId: 'CriteriaTransport-input',
          departureCity: !departureCitiesOptions.length ? '' : inputDepartureCity,
          isDisabled: !isLoading && !departureCitiesOptions.length,
          isLoading,
          labelDepartingFrom: t('faceting.transport.departingFrom'),
          optionsDepartureCities:
            !isLoading && !departureCitiesOptions.length
              ? noDepartureCitiesOptions
              : departureCitiesOptions,
          placeholderFrom: t('faceting.transport.titleFrom'),
          selectPlaceholder: t('faceting.transport.selectPlaceholder'),
          validateDepartureCity: saveTransport,
        })}
      />
    </CriteriaTrigger>
  ) : null;
};
